import { Drawer } from '@material-ui/core';
import { isEmpty } from 'lodash-es';
import React, { useState } from 'react';

import { IPresetConflict } from '~/src/dash/apiV2/targeting/targetingPresets';
import { CloseButton } from '~/src/dash/components/buttons/commonButtons/CloseButton';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { highlightedDivStyle } from '~/src/dash/components/layout/common';
import {
  borderRadius,
  ButtonV2,
  Col,
  Cols,
  Panel,
  Row,
  Rows,
  spacing,
  Text,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState } from '~/src/dash/utils/loading-states';
import { useBaseState } from '~/src/dash/views/base/contexts';
import {
  targetingConflictsDrawerConfig,
  TargetingConflictType,
} from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingConflictsDrawer/config';
import { TargetingConflictTable } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingConflictsDrawer/TargetingConflictTable';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  bidderConflicts: IPresetConflict[];
  campaignConflicts: IPresetConflict[];
  loadState: LoadingState;
  timestamp: string; // time last refreshed
  handleRefresh: () => void;
}

export const TargetingConflictsDrawer = ({
  isOpen,
  onClose,
  bidderConflicts,
  campaignConflicts,
  loadState,
  timestamp,
  handleRefresh,
}: IProps) => {
  const { totalWidth } = useBaseState();

  const [areNotesOpen, setAreNotesOpen] = useState(true);

  const extractConflicts = (type: TargetingConflictType): IPresetConflict[] => {
    switch (type) {
      case 'Bidders':
        return bidderConflicts;
      case 'Campaigns':
        return campaignConflicts;
      default:
        return [];
    }
  };

  return (
    <Drawer open={isOpen} onClose={onClose} anchor='right'>
      <Panel basePadding style={{ width: `${(totalWidth || 800) * 0.6}px`, minWidth: '600px' }}>
        <Rows gap={spacing.small}>
          <Row noPadding marginBottom={spacing.small}>
            <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              {/* TITLE & REFRESH TIME */}
              <Col>
                <Cols gap={spacing.large} style={{ alignItems: 'center' }}>
                  {/* TITLE */}
                  <Col>
                    <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                      <Col displayFlex>
                        <IconV2 name='settings' large />
                      </Col>
                      <Col>
                        <Text large bold>
                          Conflicts
                        </Text>
                      </Col>
                    </Cols>
                  </Col>
                  {/* REFRESH TIME */}
                  {timestamp && (
                    <Col>
                      <Text
                        small
                        style={{
                          backgroundColor: publicaColors.veryLightGray,
                          padding: '2px 8px',
                        }}
                      >{`Refreshed at ${timestamp}`}</Text>
                    </Col>
                  )}
                </Cols>
              </Col>
              {/* REFRESH BUTTON */}
              <Col>
                <ButtonV2 small onClick={handleRefresh}>
                  <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                    <Col displayFlex>
                      <IconV2 name='sync' color={publicaColors.white} />
                    </Col>
                    <Col>Refresh</Col>
                  </Cols>
                </ButtonV2>
              </Col>
              {/* CLOSE BUTTON */}
              <Col>
                <CloseButton onClick={onClose} />
              </Col>
            </Cols>
          </Row>
          <Row noPadding>
            <Rows
              smallVerticalPadding
              horizontalPadding
              gap={spacing.small}
              style={highlightedDivStyle}
            >
              <Row noPadding>
                <Cols
                  gap={spacing.small}
                  style={{ alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setAreNotesOpen(!areNotesOpen)}
                >
                  <Col displayFlex style={{ paddingBottom: '2px' }}>
                    <IconV2 name='light-bulb' />
                  </Col>
                  <Col>
                    <Text small bold disableSelect>
                      Understand Targeting Conflicts
                    </Text>
                  </Col>
                  <Col displayFlex>
                    <IconV2
                      name='caret'
                      style={{ transform: areNotesOpen ? 'rotate(180deg)' : 'none' }}
                    />
                  </Col>
                </Cols>
              </Row>
              {areNotesOpen && (
                <Row noPadding>
                  <Rows paddingBottom={spacing.extraSmall} gap={spacing.small}>
                    <Row noPadding>
                      <Text small bold>
                        Why Conflicts?
                      </Text>
                      <Text small>
                        {`Our validation algorithms are here to protect a steady revenue and will
                      automatically raise a conflict when the Preset you're trying to edit is
                      overlapping with active fields being targeted by Bidders or campaigns. The
                      conflict and risk here would be to set targeting for the same field twice,
                      which would block all requests and potentially hinders revenue. For example,
                      two geographic regions cannot be targeted simultaneously, a movie title cannot
                      have two distinct values, etc.`}
                      </Text>
                    </Row>
                    <Row noPadding>
                      <Text small bold>
                        Resolve The Conflicts
                      </Text>
                      <Text small>
                        • Conflicts are caused by a custom field: remove this field from the preset
                        you are editing.
                      </Text>
                      <Text small>
                        • Conflicts are caused when another preset targets the same field: to
                        combine presets, create one that does not overlap in targeting fields.
                      </Text>
                    </Row>
                  </Rows>
                </Row>
              )}
            </Rows>
          </Row>
          {Object.values(targetingConflictsDrawerConfig).map((tableCfg) => {
            const currentConflicts = extractConflicts(tableCfg.type);
            const hasConflicts = !isEmpty(currentConflicts);
            return (
              <Row
                paddingBottom={hasConflicts ? spacing.none : spacing.base}
                style={{
                  backgroundColor: publicaColors.veryLightGray,
                  borderRadius: borderRadius.base,
                }}
              >
                {hasConflicts ? (
                  <TargetingConflictTable
                    cfg={tableCfg}
                    data={extractConflicts(tableCfg.type)}
                    loadState={loadState}
                  />
                ) : (
                  <Text small bold>{`${tableCfg.nameReplacement} (0)`}</Text>
                )}
              </Row>
            );
          })}
        </Rows>
      </Panel>
    </Drawer>
  );
};
